
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { BarChart3, RefreshCw, Save, CheckCircle, AlertCircle } from 'lucide-react';
import { useSiteConfigValue } from '@/hooks/useSiteConfigValue';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';

const AdminSiteConfigSEOSettings = () => {
  const { toast } = useToast();
  const [saving, setSaving] = useState<string | null>(null);

  const seoConfigs = [
    { 
      key: 'meta_description', 
      label: 'Meta Description', 
      type: 'textarea',
      placeholder: 'Brief description of your website for search engines'
    },
    { 
      key: 'meta_keywords', 
      label: 'Meta Keywords', 
      type: 'input',
      placeholder: 'keyword1, keyword2, keyword3'
    },
    { 
      key: 'google_analytics_id', 
      label: 'Google Analytics ID', 
      type: 'input',
      placeholder: 'G-XXXXXXXXXX or UA-XXXXXXXXX'
    },
    { 
      key: 'google_tag_manager_id', 
      label: 'Google Tag Manager ID', 
      type: 'input',
      placeholder: 'GTM-XXXXXXX'
    },
    { 
      key: 'facebook_pixel_id', 
      label: 'Facebook Pixel ID', 
      type: 'input',
      placeholder: 'XXXXXXXXXXXXXXX'
    }
  ];

  const SEOConfigCard = ({ config }: { config: typeof seoConfigs[0] }) => {
    const { value, loading, refetch } = useSiteConfigValue(config.key);
    const [localValue, setLocalValue] = useState('');
    const [hasChanged, setHasChanged] = useState(false);

    React.useEffect(() => {
      if (value !== null && value !== localValue) {
        setLocalValue(value);
        setHasChanged(false);
      }
    }, [value]);

    const handleInputChange = (newValue: string) => {
      setLocalValue(newValue);
      setHasChanged(newValue !== (value || ''));
    };

    const handleUpdate = async () => {
      setSaving(config.key);
      try {
        console.log(`Updating ${config.key} to:`, localValue);
        
        const { error } = await supabase.rpc('update_site_config', {
          config_key: config.key,
          new_value: localValue,
          admin_email: null,
          change_reason: `Updated ${config.label} via Admin Panel`
        });

        if (error) throw error;

        toast({
          title: "Success",
          description: `${config.label} updated successfully`,
        });
        
        setHasChanged(false);
        await refetch();
      } catch (error) {
        console.error(`Error updating ${config.key}:`, error);
        toast({
          title: "Error",
          description: `Failed to update ${config.label}`,
          variant: "destructive",
        });
      } finally {
        setSaving(null);
      }
    };

    const handleRefresh = async () => {
      console.log(`Refreshing ${config.key}...`);
      await refetch();
    };

    return (
      <Card className="bg-white/10 backdrop-blur-md border-white/20">
        <CardHeader className="pb-3">
          <CardTitle className="text-white text-sm font-medium flex items-center justify-between">
            <span>{config.label}</span>
            <div className="flex items-center space-x-2">
              {loading ? (
                <div className="w-4 h-4 border border-white/20 border-t-white/60 rounded-full animate-spin" />
              ) : (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleRefresh}
                  className="text-gray-400 hover:text-white p-1"
                >
                  <RefreshCw className="w-3 h-3" />
                </Button>
              )}
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-white text-xs">Current Value</Label>
              <div className="flex items-center space-x-1">
                {hasChanged && <Badge className="bg-orange-600/20 text-orange-400 text-xs">Modified</Badge>}
                {localValue ? (
                  <CheckCircle className="w-3 h-3 text-green-400" />
                ) : (
                  <AlertCircle className="w-3 h-3 text-gray-400" />
                )}
              </div>
            </div>
            {config.type === 'textarea' ? (
              <Textarea
                value={localValue}
                onChange={(e) => handleInputChange(e.target.value)}
                placeholder={config.placeholder}
                disabled={saving === config.key}
                className="bg-gray-800 border-gray-600 text-white text-sm min-h-[80px]"
                rows={3}
              />
            ) : (
              <Input
                value={localValue}
                onChange={(e) => handleInputChange(e.target.value)}
                placeholder={config.placeholder}
                disabled={saving === config.key}
                className="bg-gray-800 border-gray-600 text-white text-sm"
              />
            )}
          </div>

          <div className="flex items-center justify-between">
            <div className="text-xs text-gray-400">
              {loading ? 'Loading...' : `Loaded: ${value ? (value.length > 30 ? value.substring(0, 30) + '...' : value) : 'Not set'}`}
            </div>
            <Button
              onClick={handleUpdate}
              disabled={!hasChanged || saving === config.key}
              size="sm"
              className="bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50"
            >
              {saving === config.key ? (
                <div className="w-3 h-3 border border-white/20 border-t-white/60 rounded-full animate-spin mr-1" />
              ) : (
                <Save className="w-3 h-3 mr-1" />
              )}
              Update
            </Button>
          </div>

          <div className="text-xs text-gray-500 space-y-1">
            <div>Debug Info:</div>
            <div>• Key: {config.key}</div>
            <div>• Current: {value ? (value.length > 20 ? value.substring(0, 20) + '...' : value) : 'null'}</div>
            <div>• Local: {localValue ? (localValue.length > 20 ? localValue.substring(0, 20) + '...' : localValue) : 'empty'}</div>
            <div>• Changed: {hasChanged ? 'yes' : 'no'}</div>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <Card className="bg-white/10 backdrop-blur-md border-white/20">
      <CardHeader>
        <CardTitle className="text-white flex items-center space-x-2">
          <BarChart3 className="w-5 h-5" />
          <span>SEO & Analytics Configuration</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {seoConfigs.map((config) => (
            <SEOConfigCard key={config.key} config={config} />
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export default AdminSiteConfigSEOSettings;
